<script lang="ts" setup>
import {
  AliyahType,
  HaftarahMelodyType,
  HaftarahType,
  PronunciationType,
  SentenceGroupMelodyType,
  SentenceGroupType,
  TagType,
  TorahMelodyType,
} from 'types/types'
import { computed, watch } from 'vue'

import { type PhraseLevelInfo } from '@/composables/versePhraser'
import { type SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { RecordingVoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'

// {{{ props
interface Props {
  voiceStyleSet: VoiceStyleSet
  phraseLevel: number
  availableLevels: PhraseLevelInfo[]
  aliyah: AliyahType | null
  haftarah: HaftarahType | null
  sentenceGroup: SentenceGroupType | null
  isRecording: boolean
  existingSentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios | null
  phrasingDropdownDisabled: boolean
  pronunciationChoices: Array<PronunciationType>
  torahMelodyChoices: Array<TorahMelodyType>
  haftarahMelodyChoices: Array<HaftarahMelodyType>
  sentenceGroupMelodyChoices: Array<SentenceGroupMelodyType>
  nonHiddenUserTags: Array<TagType>
  inReadMode: boolean
  isMelodicStyleDisabled: boolean
}

const props = defineProps<Props>()
// }}}

// {{{ emits
interface Emits {
  (e: 'update:voiceStyleSet', value: VoiceStyleSet): void
  (e: 'update:phraseLevel', value: number): void
}

const emit = defineEmits<Emits>()
// }}}

// {{{ debug
console.log('VoiceStyleSelector setup: phraseLevel =', props.phraseLevel, ', voice =', props.voiceStyleSet.voice)
watch(
  () => props.phraseLevel,
  (newVal, oldVal) => {
    console.log('VoiceStyleSelector: phraseLevel changed', oldVal, '->', newVal)
  },
)
// }}}

// {{{ slider position ↔ actual phrase level mapping
// The slider operates in "position space" (0 to N-1) where N is the number
// of distinct levels. Each position maps to an actual trope level via availableLevels.

const sliderMax = computed(() => Math.max(props.availableLevels.length - 1, 0))

const sliderPosition = computed(() => {
  const idx = props.availableLevels.findIndex((info) => info.level === props.phraseLevel)
  return idx >= 0 ? idx : 0
})

const thumbLabel = computed(() => {
  const info = props.availableLevels[sliderPosition.value]
  return info ? String(info.segmentCount) : ''
})

const handleSliderChange = (position: number) => {
  const info = props.availableLevels[position]
  if (info) {
    emit('update:phraseLevel', info.level)
  }
}
// }}}

// {{{ helpers
// Shared updater — merges a partial patch into the current voiceStyleSet and emits.
// All template bindings funnel through here to stay DRY.
const updateVoiceStyle = (patch: Partial<VoiceStyleSet>) => {
  emit('update:voiceStyleSet', { ...props.voiceStyleSet, ...patch })
}

// Melody selector config — DRYs the three identical v-select blocks into one.
// Returns null when no melody selector should be shown.
const melodyConfig = computed(() => {
  if (props.isMelodicStyleDisabled) {
    return null
  }
  if (props.aliyah && props.torahMelodyChoices.length > 1) {
    return { items: props.torahMelodyChoices, key: 'torahMelody' as const, modelValue: props.voiceStyleSet.torahMelody }
  }
  if (props.haftarah && props.haftarahMelodyChoices.length > 1) {
    return { items: props.haftarahMelodyChoices, key: 'haftarahMelody' as const, modelValue: props.voiceStyleSet.haftarahMelody }
  }
  if (props.sentenceGroup && props.sentenceGroupMelodyChoices.length > 1) {
    return {
      items: props.sentenceGroupMelodyChoices,
      key: 'sentenceGroupMelody' as const,
      modelValue: props.voiceStyleSet.sentenceGroupMelody,
    }
  }
  return null
})
// }}}
</script>

<template>
  <!-- Voice Style Selector -->
  <div class="voice-style-row">
    <!-- pronunciation style -->
    <v-select
      v-if="pronunciationChoices.length > 1"
      :clearable="false"
      density="compact"
      :disabled="isRecording"
      hide-details
      item-title="description"
      :items="pronunciationChoices"
      label="Pronunciation"
      :model-value="voiceStyleSet.pronunciation"
      return-object
      style="max-width: 180px"
      variant="underlined"
      @update:model-value="updateVoiceStyle({ pronunciation: $event })"
    />

    <!-- voice style (Reading / Singing) -->
    <v-btn-toggle
      color="primary"
      density="compact"
      mandatory
      :model-value="voiceStyleSet.voice"
      @update:model-value="updateVoiceStyle({ voice: $event })"
    >
      <v-btn v-for="recordingVoiceStyle in RecordingVoiceStyles" :key="recordingVoiceStyle" size="small" :value="recordingVoiceStyle">
        {{ recordingVoiceStyle }}
      </v-btn>
    </v-btn-toggle>

    <!-- phrase-level slider (Verse ← → Words), dynamically scaled to distinct levels -->
    <div v-if="availableLevels.length > 1" class="phrase-slider-wrapper">
      <v-slider
        color="primary"
        density="compact"
        :disabled="phrasingDropdownDisabled"
        hide-details
        :max="sliderMax"
        :min="0"
        :model-value="sliderPosition"
        show-ticks="always"
        step="1"
        thumb-label
        @update:model-value="handleSliderChange($event as number)"
      >
        <template #thumb-label>{{ thumbLabel }}</template>
        <template #prepend><span class="phrase-slider-label">Verse</span></template>
        <template #append><span class="phrase-slider-label">Words</span></template>
      </v-slider>
    </div>

    <!-- melodic style (torah / haftarah / sentenceGroup) -->
    <v-select
      v-if="melodyConfig"
      :clearable="false"
      density="compact"
      :disabled="isRecording || !voiceStyleSet.voice || inReadMode"
      hide-details
      item-title="description"
      :items="melodyConfig.items"
      label="Melody"
      :model-value="melodyConfig.modelValue"
      return-object
      style="max-width: 200px"
      variant="underlined"
      @update:model-value="updateVoiceStyle({ [melodyConfig.key]: $event })"
    />

    <!-- tags -->
    <v-select
      v-if="nonHiddenUserTags.length"
      chips
      density="compact"
      :disabled="isRecording"
      hide-details
      item-title="name"
      :items="nonHiddenUserTags"
      label="Style"
      :model-value="voiceStyleSet.tags"
      multiple
      return-object
      :style="[
        'max-width: 180px',
        !existingSentenceRecordingMetadataWithPitchedAudios && (!voiceStyleSet.tags || !voiceStyleSet.tags.length)
          ? 'border: 2px solid red; border-radius: 4px; padding: 2px 4px'
          : '',
      ]"
      variant="underlined"
      @update:model-value="updateVoiceStyle({ tags: $event })"
    />
  </div>
</template>

<style scoped>
.voice-style-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  flex-wrap: wrap;
  padding: 8px 0;
}

.voice-style-row :deep(.v-btn-toggle) {
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 6px;
}

.phrase-slider-wrapper {
  min-width: 280px;
  max-width: 360px;
}

.phrase-slider-label {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: nowrap;
}
</style>
